import React, { useEffect, useId, useMemo, useRef, useState } from "react";

import { Search, X } from "lucide-react";

export interface AccountOption {
  name: string;
  /** Shown on the right of the option, e.g. "3 signals". */
  hint?: string;
}

interface Props {
  /** The search text; it filters the list by substring, case-insensitive. */
  value: string;
  onChange: (value: string) => void;
  options: AccountOption[];
}

/**
 * A search box with its own suggestion list (not a native <datalist>, whose
 * look depends on the OS). Typing filters both the list and, through
 * `onChange`, whatever the parent shows; picking an option sets the exact
 * name. ↑/↓ move, Enter picks, Esc closes.
 */
const AccountSearchFilter: React.FC<Props> = ({ value, onChange, options }) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const query = value.trim().toLowerCase();
  const exact = options.some(o => o.name === value);
  const shown = useMemo(
    () =>
      exact || !query
        ? options
        : options.filter(o => o.name.toLowerCase().includes(query)),
    [options, query, exact]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const pick = (name: string) => {
    onChange(name);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        setActive(0);
        return;
      }
      if (!shown.length) return;
      const step = e.key === "ArrowDown" ? 1 : shown.length - 1;
      setActive(i => (i + step) % shown.length);
    } else if (e.key === "Enter" && open && shown[active]) {
      e.preventDefault();
      pick(shown[active].name);
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  const highlight = (name: string) => {
    const i = query && !exact ? name.toLowerCase().indexOf(query) : -1;
    if (i < 0) return name;
    return (
      <>
        {name.slice(0, i)}
        <mark className="bg-transparent font-extrabold text-gray-900">
          {name.slice(i, i + query.length)}
        </mark>
        {name.slice(i + query.length)}
      </>
    );
  };

  const optionId = (i: number) => `${listId}-opt-${i}`;

  return (
    <div
      ref={boxRef}
      className="relative flex h-9 min-w-0 max-w-[280px] flex-[1_1_220px] items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-2.5 focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20"
    >
      <Search className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-label="Search accounts"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={
          open && shown[active] ? optionId(active) : undefined
        }
        autoComplete="off"
        spellCheck={false}
        placeholder="Search accounts"
        value={value}
        onChange={e => {
          onChange(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => {
          setOpen(true);
          setActive(0);
        }}
        onClick={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className="h-full w-full min-w-0 border-0 bg-transparent text-[12.5px] text-gray-800 outline-none placeholder:text-gray-400"
      />
      {value && (
        <button
          type="button"
          aria-label="Clear account search"
          onClick={() => {
            onChange("");
            setOpen(true);
            setActive(0);
            inputRef.current?.focus();
          }}
          className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-gray-100 text-gray-500 hover:text-gray-800"
        >
          <X className="h-3 w-3" aria-hidden />
        </button>
      )}
      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-label="Accounts"
          className="absolute left-[-1px] right-[-1px] top-[calc(100%+4px)] z-20 m-0 max-h-72 min-w-[240px] list-none overflow-y-auto rounded-lg border border-gray-200 bg-white p-1 shadow-lg"
        >
          {shown.length === 0 ? (
            <li className="px-2.5 py-2 text-[12.5px] text-gray-500">
              No account matches “{value.trim()}”
            </li>
          ) : (
            shown.map((o, i) => (
              <li
                key={o.name}
                id={optionId(i)}
                role="option"
                aria-selected={o.name === value}
                onMouseDown={e => e.preventDefault()}
                onClick={() => pick(o.name)}
                onMouseEnter={() => setActive(i)}
                className={`flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-[12.5px] ${
                  i === active ? "bg-gray-100" : ""
                }`}
              >
                <span
                  className={`min-w-0 flex-1 truncate ${
                    o.name === value ? "font-bold text-brand" : "text-gray-800"
                  }`}
                >
                  {highlight(o.name)}
                </span>
                {o.hint && (
                  <span className="shrink-0 text-[11px] text-gray-400">
                    {o.hint}
                  </span>
                )}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

export default AccountSearchFilter;
